import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Brain, 
  Sparkles, 
  Users, 
  CheckCircle2, 
  ArrowRight, 
  ArrowUpRight,
  Zap,
  Globe,
  ShieldCheck,
  Award,
  Search,
  Book,
  Activity,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { db } from '@/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const TeacherLanding = () => {
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [pricingState, setPricingState] = useState({
      monthly: { individual: "", school: "", label: "KSH/MO", cycleLabel: "Monthly" },
      termly:  { individual: "", school: "", label: "KSH/TERM", cycleLabel: "Termly" },
      yearly:  { individual: "", school: "", label: "KSH/YR", cycleLabel: "Yearly" },
  });
  const [trialDays, setTrialDays] = useState(14);
  const [landingCycle, setLandingCycle] = useState<'monthly' | 'termly' | 'yearly'>('monthly');

  useEffect(() => {
      const fetchPricing = async () => {
          try {
              const docRef = doc(db, 'systemConfig', 'independentPricing');
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                  const data = docSnap.data();
                  setPricingState(prev => ({
                      monthly: { ...prev.monthly, individual: data.monthly?.individual ?? prev.monthly.individual, school: data.monthly?.school ?? prev.monthly.school },
                      termly:  { ...prev.termly,  individual: data.termly?.individual  ?? prev.termly.individual,  school: data.termly?.school  ?? prev.termly.school  },
                      yearly:  { ...prev.yearly,  individual: data.yearly?.individual  ?? prev.yearly.individual,  school: data.yearly?.school  ?? prev.yearly.school  },
                  }));
                  setPricingLoaded(true);
              }
          } catch (error) {
              console.error("Error fetching pricing:", error);
          }
      };
      
      const fetchTrialConfig = async () => {
          try {
              const configRef = doc(db, 'system_config', 'independent_settings');
              const configSnap = await getDoc(configRef);
              if (configSnap.exists() && configSnap.data().trialDays) {
                  setTrialDays(configSnap.data().trialDays);
              }
          } catch (e) {
              console.error('Failed to fetch trial config:', e);
          }
      };

      fetchPricing();
      fetchTrialConfig();
  }, []);

  // Prevent horizontal scroll and handle mobile menu scroll lock
  useEffect(() => {
    // Persistent fix for horizontal overflow on desktop
    document.documentElement.style.overflowX = 'hidden';
    document.body.style.overflowX = 'hidden';

    if (mobileMenuOpen) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'unset';
    }

    return () => {
      document.documentElement.style.overflowX = 'auto';
      document.body.style.overflowX = 'auto';
      document.body.style.overflowY = 'unset';
    };
  }, [mobileMenuOpen]);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-white text-black selection:bg-black selection:text-white font-sans overflow-x-hidden scroll-smooth">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 border-b border-blue-600/20 px-4 md:px-6 lg:px-12 py-4 flex justify-between items-center transition-all duration-300 ${mobileMenuOpen ? 'bg-white' : 'bg-white/90 md:bg-white/70 backdrop-blur-xl'}`}>
        <div className="flex items-center">
          <span className="text-2xl tracking-tighter uppercase leading-none flex items-baseline gap-1 group">
            <span className="font-serif italic lowercase tracking-tight text-3xl text-black">sonoma</span>
            <span className="font-bold tracking-[0.2em] text-[10px] text-blue-600">Workspace</span>
          </span>
        </div>
        
        <div className="hidden lg:flex items-center gap-12">
          <div className="flex gap-8 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-500">
            <a href="#features" className="hover:text-black transition-colors">Workspace</a>
            <a href="#vision" className="hover:text-black transition-colors">Vision</a>
            <a href="#pricing" className="hover:text-black transition-colors">Pricing</a>
            <a href="#impact" className="hover:text-black transition-colors">Impact</a>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 hover:text-black transition-all">
              Login
            </Link>
            <Link to="/signup">
              <button className="text-[10px] font-bold uppercase tracking-[0.2em] border border-black px-8 py-2.5 rounded-full hover:bg-black hover:text-white transition-all">
                Sign Up
              </button>
            </Link>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="lg:hidden p-2 text-black"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-0 z-[100] bg-white p-8 flex flex-col justify-between"
            >
              <div className="flex justify-between items-center mb-16">
                <span className="text-2xl tracking-tighter uppercase leading-none flex items-baseline gap-1">
                  <span className="font-serif italic lowercase tracking-tight text-3xl text-black">sonoma</span>
                  <span className="font-bold tracking-[0.2em] text-[10px] text-blue-600">Workspace</span>
                </span>
                <button onClick={() => setMobileMenuOpen(false)}><X size={24} /></button>
              </div>

              <div className="flex flex-col gap-8">
                {['Workspace', 'Vision', 'Pricing', 'Impact'].map((item) => (
                  <a 
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-4xl font-medium tracking-tighter text-black hover:italic transition-all"
                  >
                    {item}
                  </a>
                ))}
              </div>

              <div className="flex flex-col gap-4">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="w-full text-center py-4 border border-black/10 text-[10px] font-bold uppercase tracking-[0.2em]">
                  Login
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="w-full text-center py-4 bg-black text-white text-[10px] font-bold uppercase tracking-[0.2em]">
                  Get Started
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section - Split Screen Vertical */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-24">
        <div className="relative h-[50vh] lg:h-auto overflow-hidden bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-100">
          <motion.img 
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop" 
            className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000" 
            alt="Hero Workspace"
          />
          <div className="absolute bottom-6 left-6 md:bottom-12 md:left-12 p-6 md:p-10 bg-white/10 backdrop-blur-xl border border-white/20 text-white max-w-sm hidden md:block">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-4 opacity-70">
              EST. 2026 // EMPOWERING EDUCATORS // BY SONOMA LABS
            </div>
            <h2 className="text-2xl font-light italic leading-tight">
              A minimalist approach to teaching, focusing on depth, clarity, and innovation.
            </h2>
          </div>
        </div>
        
        <div className="flex flex-col justify-center p-8 md:p-12 lg:p-32 bg-white">
          <motion.span 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-6 md:mb-8 uppercase"
          >
            01 // CORE INTRODUCTION
          </motion.span>
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl md:text-8xl lg:text-9xl font-medium tracking-tighter leading-[0.85] mb-8 md:mb-12"
          >
            Teach With <br/>
            <span className="text-blue-600 italic">Purpose.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-2xl text-gray-500 max-w-md font-light leading-snug mb-10 md:mb-16"
          >
            Sonoma Workspace harmonizes advanced artificial intelligence with your expertise to deliver a focused, efficient teaching environment.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-8"
          >
            <Link to="/signup" className="flex items-center justify-between w-full sm:w-64 gap-2 text-[10px] font-black uppercase tracking-[0.3em] border-b border-blue-600 pb-4 text-blue-600 hover:text-blue-400 hover:border-blue-400 transition-all group">
              Start Free Trial <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            <a href="#features" className="flex items-center justify-between w-full sm:w-64 gap-2 text-[10px] font-black uppercase tracking-[0.3em] border-b border-black/10 pb-4 hover:border-black transition-all">
              Explore Workspace <ArrowRight size={18} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Stats - Horizontal Strip */}
      <section id="impact" className="py-20 md:py-32 px-6 md:px-12 border-t border-gray-100 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase italic">02 // BY THE NUMBERS</span>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-12 md:gap-16 lg:gap-32">
              {[
                { value: "50%", label: "Time Saved Weekly" },
                { value: "10k+", label: "Lessons Generated" },
                { value: "98%", label: "Teacher Satisfaction" }
              ].map((stat, i) => (
                <div key={i} className="group">
                  <div className="text-5xl md:text-9xl font-light tracking-tighter mb-4 group-hover:italic transition-all">
                    {stat.value}
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Editorial Layout */}
      <section id="vision" className="py-20 md:py-40 px-6 md:px-12 bg-gray-50 border-y border-gray-100">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            <div className="lg:col-span-6">
              <div className="relative group overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop" 
                  className="w-full aspect-[4/5] object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                  alt="Teaching Vision"
                />
                <div className="absolute top-0 right-0 w-32 h-32 bg-white flex items-center justify-center -translate-y-1/2 translate-x-1/2 shadow-2xl rotate-12">
                  <Sparkles size={48} className="text-gray-200" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-8 block uppercase">03 // OUR PHILOSOPHY</span>
              <h2 className="text-3xl md:text-7xl font-medium tracking-tighter leading-tight mb-8 md:mb-12">
                Simplicity in <br/><span className="text-blue-600 italic">Excellence.</span>
              </h2>
              <div className="text-lg md:text-2xl font-light text-gray-500 leading-relaxed mb-10 md:mb-16 italic">
                We believe that artificial intelligence shouldn't replace the teacher—it should strip away the noise so the teacher can focus on the heart of education.
              </div>
              <div className="grid grid-cols-2 gap-4 md:gap-8 mb-10 md:mb-16">
                <div className="p-6 md:p-10 border border-black/5 bg-white">
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4 text-gray-300">Workspace</div>
                  <p className="text-xs text-gray-500 leading-relaxed font-medium uppercase tracking-widest">Simple, intuitive tools designed for maximum classroom impact.</p>
                </div>
                <div className="p-6 md:p-10 border border-blue-100 bg-white">
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4 text-blue-500">Intelligence</div>
                  <p className="text-xs text-gray-500 leading-relaxed font-medium uppercase tracking-widest">Driven by cutting-edge neural architectures for unparalleled academic precision.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing / Subscriptions Section */}
      <section id="pricing" className="py-20 md:py-40 px-6 md:px-12 bg-white border-y border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">05 // INVESTMENT</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-4xl md:text-8xl font-medium tracking-tighter italic">Simple Pricing.</h2>
            </div>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-16">
            <div className="inline-flex border border-gray-200 bg-gray-50">
              {(['monthly', 'termly', 'yearly'] as const).map((cycle) => {
                let savings = 0;
                if (cycle !== 'monthly') {
                  const getPrice = (val: any) => typeof val === 'number' ? val : parseInt(String(val).replace(/,/g, ''), 10) || 0;
                  const monthlyPrice = getPrice(pricingState.monthly.individual);
                  const cyclePrice = getPrice(pricingState[cycle].individual);
                  
                  const multiplier = cycle === 'termly' ? 3 : 12;
                  const baseline = monthlyPrice * multiplier;
                  
                  if (baseline > 0 && cyclePrice > 0 && cyclePrice < baseline) {
                    savings = Math.round(((baseline - cyclePrice) / baseline) * 100);
                  }
                }

                return (
                  <button
                    key={cycle}
                    onClick={() => setLandingCycle(cycle)}
                    className={`px-5 md:px-8 py-3 text-[10px] font-black uppercase tracking-[0.25em] transition-all ${
                      landingCycle === cycle
                        ? 'bg-black text-white'
                        : 'text-gray-400 hover:text-black'
                    }`}
                  >
                    {cycle === 'monthly' ? 'Monthly' : cycle === 'termly' ? 'Termly' : 'Yearly'}
                    {savings > 0 && <span className="ml-2 text-[8px] text-emerald-500 font-black">SAVE {savings}%</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-100 border border-gray-100">
              {/* Individual Trial Card */}
              {landingCycle === 'monthly' && (
              <div className="bg-white p-8 md:p-16 flex flex-col justify-between">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase mb-8">TRIAL</div>
                  <h3 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">Individual Trial</h3>
                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="text-6xl font-medium tracking-tighter">Free</span>
                    <span className="text-gray-400 text-sm font-light">/ {trialDays} DAYS</span>
                  </div>
                  <ul className="space-y-4 mb-12">
                    {["AI Lesson Planning & Assessments", "Unlimited Research Lab Access", "Resource Library", "PDF Converter & Utilities"].map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        <CheckCircle2 size={14} className="text-black" /> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/signup">
                  <button className="w-full text-[10px] font-black uppercase tracking-[0.3em] border border-black py-5 hover:bg-black hover:text-white transition-all">
                    Begin Trial
                  </button>
                </Link>
              </div>
              )}

              {/* Individual Plan Card */}
              <div className="bg-black text-white p-8 md:p-16 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-20 rotate-12">
                  <Award size={120} />
                </div>
                <div className="relative z-10">
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-500 uppercase mb-8">INDIVIDUAL</div>
                  <h3 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">Individual Plan</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-6xl font-medium tracking-tighter">
                      {pricingLoaded ? `KSh ${pricingState[landingCycle].individual}` : '---'}
                    </span>
                    <span className="text-gray-500 text-sm font-light">/ {pricingState[landingCycle].label}</span>
                  </div>
                  <p className="text-gray-600 text-[10px] font-bold uppercase tracking-widest mb-8">{pricingState[landingCycle].cycleLabel} billing</p>
                  <p className="text-gray-400 text-sm font-light mb-12 italic">
                    Unrestricted access to the most advanced teaching AI ever built. Seamlessly billed after your first month.
                  </p>
                </div>
                <Link to="/signup">
                  <button className="w-full text-[10px] font-black uppercase tracking-[0.3em] border border-white py-5 hover:bg-white hover:text-black transition-all">
                    Upgrade Now
                  </button>
                </Link>
              </div>

              {/* Faculty Trial Card */}
              {landingCycle === 'monthly' && (
              <div className="bg-white p-8 md:p-16 flex flex-col justify-between">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase mb-8">TRIAL</div>
                  <h3 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">Faculty Trial</h3>
                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="text-6xl font-medium tracking-tighter">Free</span>
                    <span className="text-gray-400 text-sm font-light">/ {trialDays} DAYS</span>
                  </div>
                  <ul className="space-y-4 mb-12">
                    {["All Individual Features", "Faculty Management Suite", "Custom School Website", "Year-round Security & Maintenance", "On-Demand Site Support"].map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        <CheckCircle2 size={14} className="text-black" /> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/signup">
                  <button className="w-full text-[10px] font-black uppercase tracking-[0.3em] border border-black py-5 hover:bg-black hover:text-white transition-all">
                    Begin Trial
                  </button>
                </Link>
              </div>
              )}

              {/* Faculty Plan Card */}
              <div className="bg-black text-white p-8 md:p-16 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-20 rotate-12">
                  <Users size={120} />
                </div>
                <div className="relative z-10">
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-500 uppercase mb-8">FACULTY</div>
                  <h3 className="text-3xl md:text-4xl font-light tracking-tighter mb-4">Faculty Plan</h3>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-6xl font-medium tracking-tighter">
                      {pricingLoaded ? `KSh ${pricingState[landingCycle].school}` : '---'}
                    </span>
                    <span className="text-gray-500 text-sm font-light">/ {pricingState[landingCycle].label}</span>
                  </div>
                  <p className="text-gray-600 text-[10px] font-bold uppercase tracking-widest mb-8">{pricingState[landingCycle].cycleLabel} billing</p>
                  <p className="text-gray-400 text-sm font-light mb-12 italic">
                    Full access for your entire teaching staff, plus we design and build a custom school website — maintained, secured and updated all year round.
                  </p>
                </div>
                <Link to="/signup">
                  <button className="w-full text-[10px] font-black uppercase tracking-[0.3em] border border-white py-5 hover:bg-white hover:text-black transition-all">
                    Upgrade Now
                  </button>
                </Link>
              </div>
            </div>
            
            <div className="mt-16 text-center">
              <p className="text-[10px] font-bold tracking-[0.4em] text-gray-300 uppercase">
                NO HIDDEN COSTS // CANCEL ANYTIME // BUILT FOR SCHOOLS
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Features - Clean Grid */}
      <section id="features" className="py-20 md:py-40 px-6 md:px-12 bg-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-24">
            <div className="max-w-xl">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-6 block uppercase italic">04 // YOUR TEACHING TOOLKIT</span>
              <h2 className="text-4xl md:text-8xl font-medium tracking-tighter">Refined Tools.</h2>
            </div>
            <div className="text-right">
              <p className="text-gray-300 font-mono text-[10px] uppercase mb-4 tracking-[0.2em]">BUILT FOR EDUCATORS // READY FOR THE CLASSROOM</p>
              <Link to="/signup">
                <button className="text-[10px] font-bold uppercase tracking-[0.2em] border border-black px-12 py-5 hover:bg-black hover:text-white transition-all">Get Full Access</button>
              </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-gray-100">
            {[
              { 
                title: "AI Research Lab", 
                desc: "Deep research across academic sources with automatic citation generation and source credibility analysis.",
                icon: Search,
                label: "RESEARCH // 01"
              },
              { 
                title: "Dynamic Planner", 
                desc: "Create comprehensive lesson plans and exam questions in seconds. Tailored to your specific curriculum and goals.",
                icon: Book,
                label: "PLANNING // 02"
              },
              { 
                title: "Growth Analytics", 
                desc: "Track attendance, individual performance, and behavioral trends with intuitive dashboard analytics.",
                icon: Activity,
                label: "INSIGHTS // 03"
              }
            ].map((feature, i) => (
              <div key={i} className="border-r border-b border-gray-100 p-8 md:p-20 hover:bg-gray-50 transition-colors group relative overflow-hidden text-center">
                <div className="mb-8 md:mb-14 aspect-square bg-gray-50 flex items-center justify-center grayscale group-hover:grayscale-0 transition-all duration-700">
                  <feature.icon className="w-12 h-12 md:w-16 md:h-16 text-gray-200 group-hover:text-black transition-colors" />
                </div>
                <div className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase mb-4 italic">
                  {feature.label}
                </div>
                <h3 className="text-3xl font-medium tracking-tighter mb-8 group-hover:italic transition-all">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto mb-12">
                  {feature.desc}
                </p>
                <ArrowRight className="mx-auto text-gray-200 group-hover:text-black group-hover:translate-x-3 transition-all" size={24} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Dramatic Inversion */}
      <section className="bg-black text-white py-32 md:py-64 px-6 md:px-12 text-center">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full mx-auto mb-10 md:mb-16 flex items-center justify-center"
          >
            <Sparkles size={32} className="text-black md:hidden" />
            <Sparkles size={40} className="text-black hidden md:block" />
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl md:text-[8rem] lg:text-[10rem] font-medium tracking-tighter leading-none mb-10 md:mb-16"
          >
            Start Your <br/><span className="italic block -translate-y-4 md:-translate-y-8">Legacy.</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-base md:text-3xl font-light text-gray-500 max-w-2xl mx-auto mb-12 md:mb-20 leading-relaxed italic px-4"
          >
            Join a community of forward-thinking educators embracing the fusion of human insight and machine intelligence.
          </motion.p>
          <div className="flex flex-col sm:flex-row justify-center gap-6 md:gap-12 px-4">
            <Link to="/signup" className="text-[11px] font-black uppercase tracking-[0.5em] border border-white px-10 md:px-20 py-6 md:py-8 hover:bg-white hover:text-black transition-all group text-center">
              Join Now <ArrowRight className="inline-block ml-4 group-hover:translate-x-4 transition-transform" size={20} />
            </Link>
            <Link to="/login" className="text-[11px] font-black uppercase tracking-[0.5em] text-gray-500 hover:text-white transition-colors py-6 md:py-8 text-center">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer - Extreme Minimalism */}
      <footer className="bg-white pt-20 md:pt-40 pb-12 md:pb-20 px-6 md:px-12 border-t border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-24 lg:gap-8 mb-40">
            <div className="lg:col-span-2">
              <div className="text-2xl tracking-tighter uppercase mb-2 flex items-baseline gap-1">
                <span className="font-serif italic lowercase tracking-tight text-3xl text-black">sonoma</span>
                <span className="font-bold tracking-[0.2em] text-[10px] text-blue-600">Workspace.</span>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-blue-500 mb-8 italic">A PRODUCT OF SONOMA LABS</div>
              <p className="text-sm text-gray-600 leading-relaxed italic max-w-xs">"Refining the classroom experience through purposeful artificial intelligence."</p>
            </div>
            
            <div>
              <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-gray-400 mb-10 italic">WORKAREAS</h4>
              <ul className="space-y-6 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                <li className="hover:text-black transition-all cursor-pointer">Research Lab</li>
                <li className="hover:text-black transition-all cursor-pointer">Lesson Planning</li>
                <li className="hover:text-black transition-all cursor-pointer">Exam Generation</li>
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-gray-400 mb-10 italic">LEGAL</h4>
              <ul className="space-y-6 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                <li><Link to="/privacy-policy" className="hover:text-black transition-all cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-black transition-all cursor-pointer">Terms of Service</Link></li>
              </ul>
            </div>


          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-12 pt-20 border-t border-gray-50">
            <div className="flex gap-12 text-gray-300">
              <Award size={14} className="hover:text-black cursor-pointer shadow-sm" />
            </div>
            <div className="text-[8px] font-bold uppercase tracking-[0.5em] text-gray-600 text-center md:text-right">
              &copy; {new Date().getFullYear()} Sonoma Workspace // POWERED BY SONOMA LABS
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TeacherLanding;
